<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import ConversationApi from 'dashboard/api/inbox/conversation';
import { dynamicTime, messageTimestamp } from 'shared/helpers/timeHelper';

const { t } = useI18n();
const showAlert = useAlert;
const { accountScopedRoute } = useAccount();

const keyword = ref('');
const isLoading = ref(false);
const rows = ref([]);
const selectedAgent = ref('');

const normalizedKeyword = computed(() => String(keyword.value || '').trim());
const agentCounts = computed(() => {
  const countsByAgent = new Map();
  rows.value.forEach(row => {
    countsByAgent.set(
      row.agentName,
      (countsByAgent.get(row.agentName) || 0) + 1
    );
  });

  return Array.from(countsByAgent.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
});
const filteredRows = computed(() => {
  if (!selectedAgent.value) return rows.value;

  return rows.value.filter(row => row.agentName === selectedAgent.value);
});
const totalMatches = computed(() => filteredRows.value.length);
const totalAllMatches = computed(() => rows.value.length);

const formatMatchedAt = unixTime => {
  const timestamp = Number(unixTime || 0);
  if (!timestamp) return '-';
  return `${messageTimestamp(timestamp, 'MMM d, h:mm a')} (${dynamicTime(timestamp)})`;
};

const buildConversationRoute = ({ conversationId, messageId }) =>
  accountScopedRoute(
    'inbox_conversation',
    { conversation_id: conversationId },
    messageId ? { messageId } : {}
  );

const fetchAllSearchResults = async query => {
  const perPage = 25;
  const all = [];
  let page = 1;

  while (true) {
    const response = await ConversationApi.search({ q: query, page });
    const payload = response?.data?.payload || [];
    if (!payload.length) break;
    all.push(...payload);
    if (payload.length < perPage) break;
    page += 1;
  }

  const uniqueById = new Map();
  all.forEach(conversation =>
    uniqueById.set(Number(conversation.id), conversation)
  );
  return Array.from(uniqueById.values());
};

const runSearch = async () => {
  if (!normalizedKeyword.value) return;

  isLoading.value = true;
  rows.value = [];
  selectedAgent.value = '';

  try {
    const query = normalizedKeyword.value.toLowerCase();
    const conversations = await fetchAllSearchResults(normalizedKeyword.value);
    const results = [];

    conversations.forEach(conversation => {
      const conversationId = Number(conversation.id || 0);
      const messages = conversation.messages || [];
      messages.forEach(message => {
        const isOutgoing = Number(message.message_type) === 1;
        const content = String(message.content || '');
        if (!isOutgoing || !content.toLowerCase().includes(query)) return;

        const agentName = String(
          message.sender_name || t('QA_DASHBOARD.KEYWORD.UNKNOWN_AGENT')
        );
        const messageId = Number(message.id || 0);
        results.push({
          id: `${conversationId}-${messageId || results.length}`,
          conversationId,
          messageIdLabel: messageId || '-',
          agentName,
          preview: content || '-',
          createdAt: Number(message.created_at || 0),
          matchedAt: formatMatchedAt(message.created_at),
          conversationRoute: buildConversationRoute({
            conversationId,
            messageId,
          }),
        });
      });
    });

    rows.value = results.sort((a, b) => b.createdAt - a.createdAt);
  } catch (error) {
    showAlert(
      error.response?.data?.error || t('QA_DASHBOARD.KEYWORD.ERROR_RUN_SEARCH')
    );
  } finally {
    isLoading.value = false;
  }
};

const setSelectedAgent = agentName => {
  selectedAgent.value = String(agentName || '');
};
</script>

<template>
  <div class="qks-page">
    <div class="qks-shell">
      <div class="qks-header">
        <div class="qks-header-left">
          <button
            class="qks-side-toggle"
            type="button"
            aria-label="Toggle side navigation"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              width="16"
              height="16"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 4v16M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z"
              />
            </svg>
          </button>
          <h1 class="qks-title">{{ t('QA_DASHBOARD.KEYWORD.TITLE') }}</h1>
        </div>
        <button class="qks-save-btn" type="button" disabled>Save view</button>
      </div>

      <div class="qks-content">
        <section class="qks-card">
          <div class="qks-card-header">
            <div class="qks-card-title-wrap">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8h.01M11 12h1v4h1M3 12a9 9 0 1 0 18.001 0A9 9 0 0 0 3 12"
                />
              </svg>
              <h2 class="qks-card-title">
                {{ t('QA_DASHBOARD.KEYWORD.TITLE') }}
              </h2>
            </div>
          </div>
          <div class="qks-section">
            <p class="qks-desc">{{ t('QA_DASHBOARD.KEYWORD.DESCRIPTION') }}</p>
          </div>
        </section>

        <section class="qks-card">
          <div class="qks-card-header">
            <div class="qks-card-title-wrap">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M10 10a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm0 0c5.523 0 10 2.015 10 4.5S15.523 19 10 19 0 16.985 0 14.5 4.477 10 10 10Z"
                />
              </svg>
              <h2 class="qks-card-title">
                {{ t('QA_DASHBOARD.KEYWORD.SEARCH') }}
              </h2>
            </div>
            <div class="qks-actions">
              <button
                type="button"
                class="qks-primary-btn"
                :disabled="isLoading || !normalizedKeyword"
                @click="runSearch"
              >
                {{
                  isLoading
                    ? t('QA_DASHBOARD.FORM.RUNNING')
                    : t('QA_DASHBOARD.KEYWORD.SEARCH')
                }}
              </button>
            </div>
          </div>

          <div class="qks-section">
            <div class="qks-form-grid">
              <div class="qks-field qks-field--span-8">
                <label class="qks-label">{{
                  t('QA_DASHBOARD.KEYWORD.INPUT_LABEL')
                }}</label>
                <input
                  v-model="keyword"
                  class="qks-input"
                  :placeholder="t('QA_DASHBOARD.KEYWORD.INPUT_PLACEHOLDER')"
                  @keydown.enter="runSearch"
                />
              </div>
              <div class="qks-field qks-field--span-4">
                <label class="qks-label">{{
                  t('QA_DASHBOARD.KEYWORD.AGENT_BREAKDOWN')
                }}</label>
                <select
                  class="qks-input"
                  :value="selectedAgent"
                  @change="setSelectedAgent($event.target.value)"
                >
                  <option value="">
                    {{ t('QA_DASHBOARD.KEYWORD.ALL_AGENTS') }} ({{
                      totalAllMatches
                    }})
                  </option>
                  <option
                    v-for="agent in agentCounts"
                    :key="agent.name"
                    :value="agent.name"
                  >
                    {{ agent.name }} ({{ agent.count }})
                  </option>
                </select>
              </div>
            </div>

            <div class="qks-chip-row">
              <button
                type="button"
                class="qks-chip"
                :class="selectedAgent ? '' : 'qks-chip--active'"
                @click="setSelectedAgent('')"
              >
                {{ t('QA_DASHBOARD.KEYWORD.ALL_AGENTS') }}
                <span class="qks-chip-count">{{ totalAllMatches }}</span>
              </button>

              <button
                v-for="agent in agentCounts"
                :key="agent.name"
                type="button"
                class="qks-chip"
                :class="selectedAgent === agent.name ? 'qks-chip--active' : ''"
                @click="setSelectedAgent(agent.name)"
              >
                {{ agent.name }}
                <span class="qks-chip-count">{{ agent.count }}</span>
              </button>

              <span v-if="!agentCounts.length" class="qks-muted">
                {{ t('QA_DASHBOARD.TABLE.EMPTY') }}
              </span>
            </div>
          </div>
        </section>

        <div class="qks-metrics">
          <div class="qks-metric">
            <div class="qks-metric-label">
              {{ t('QA_DASHBOARD.KEYWORD.TOTAL_MATCHES') }}
            </div>
            <div class="qks-metric-value">{{ totalMatches }}</div>
          </div>
          <div class="qks-metric">
            <div class="qks-metric-label">
              {{ t('QA_DASHBOARD.KEYWORD.AGENT_BREAKDOWN') }}
            </div>
            <div class="qks-metric-value">
              {{ selectedAgent || t('QA_DASHBOARD.KEYWORD.ALL_AGENTS') }}
            </div>
          </div>
        </div>

        <section class="qks-card">
          <div class="qks-card-header">
            <div class="qks-card-title-wrap">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 7h13M8 12h13M8 17h13M3 7h.01M3 12h.01M3 17h.01"
                />
              </svg>
              <h2 class="qks-card-title">Results</h2>
            </div>
          </div>

          <div class="qks-table-wrap">
            <table class="qks-table">
              <thead>
                <tr>
                  <th class="qks-th">
                    {{ t('QA_DASHBOARD.TABLE.CONVERSATION') }}
                  </th>
                  <th class="qks-th">{{ t('QA_DASHBOARD.KEYWORD.AGENT') }}</th>
                  <th class="qks-th">
                    {{ t('QA_DASHBOARD.KEYWORD.MATCHED_AT') }}
                  </th>
                  <th class="qks-th">
                    {{ t('QA_DASHBOARD.KEYWORD.PREVIEW') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="isLoading">
                  <td class="qks-td qks-td--empty" colspan="4">
                    <div class="qks-loading-row">
                      <div class="qks-spinner" />
                      <span>{{ t('QA_DASHBOARD.FORM.RUNNING') }}</span>
                    </div>
                  </td>
                </tr>

                <tr v-for="row in filteredRows" :key="row.id">
                  <td class="qks-td">
                    <RouterLink class="qks-link" :to="row.conversationRoute">
                      #{{ row.conversationId }}
                    </RouterLink>
                    <div class="qks-subtle">
                      {{ t('QA_DASHBOARD.KEYWORD.MESSAGE_ID') }}:
                      {{ row.messageIdLabel }}
                    </div>
                  </td>
                  <td class="qks-td">{{ row.agentName }}</td>
                  <td class="qks-td">{{ row.matchedAt }}</td>
                  <td class="qks-td qks-td--pre">
                    <RouterLink class="qks-link" :to="row.conversationRoute">
                      {{ row.preview }}
                    </RouterLink>
                  </td>
                </tr>

                <tr v-if="!filteredRows.length && !isLoading">
                  <td class="qks-td qks-td--empty" colspan="4">
                    {{ t('QA_DASHBOARD.TABLE.EMPTY') }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<style scoped>
.qks-page {
  background: #ffffff;
}

.qks-shell {
  max-width: 1180px;
  margin: 0 auto;
  padding: 16px 0 40px;
}

.qks-header {
  position: sticky;
  top: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 0;
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 16px;
}

.qks-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.qks-side-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #111827;
}

.qks-title {
  margin: 0;
  font-size: 18px;
  line-height: 1.2;
  font-weight: 700;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.qks-save-btn {
  height: 32px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #6b7280;
  font-weight: 700;
  font-size: 12px;
}

.qks-content {
  display: grid;
  gap: 16px;
}

.qks-card {
  border-radius: 4px;
  border: 1px solid #d8dee5;
  background: #ffffff;
  overflow: hidden;
}

.qks-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid #e5e7eb;
}

.qks-card-title-wrap {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  color: #111827;
}

.qks-card-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.qks-actions {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-shrink: 0;
}

.qks-section {
  padding: 16px 20px 20px;
}

.qks-desc {
  margin: 0;
  color: #6b7280;
  font-size: 12px;
  line-height: 1.6;
}

.qks-form-grid {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 12px;
}

.qks-field {
  display: grid;
  gap: 6px;
  min-width: 0;
}

.qks-field--span-8 {
  grid-column: span 8;
}

.qks-field--span-4 {
  grid-column: span 4;
}

.qks-label {
  font-size: 12px;
  font-weight: 700;
  color: #111827;
}

.qks-input {
  width: 100%;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #111827;
  padding: 10px 10px;
  font-size: 12px;
  outline: none;
}

.qks-input:focus {
  border-color: rgba(59, 130, 246, 0.55);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
}

.qks-primary-btn {
  height: 32px;
  padding: 0 12px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 800;
  border: 1px solid transparent;
  cursor: pointer;
  white-space: nowrap;
  background: var(--n-brand);
  color: #ffffff;
}

.qks-primary-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.qks-chip-row {
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.qks-chip {
  height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #111827;
  font-size: 11px;
  font-weight: 800;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.qks-chip--active {
  background: rgba(59, 130, 246, 0.12);
  border-color: rgba(59, 130, 246, 0.28);
  color: #1d4ed8;
}

.qks-chip-count {
  height: 18px;
  padding: 0 8px;
  border-radius: 999px;
  background: rgba(17, 24, 39, 0.06);
  color: #111827;
  font-size: 11px;
  font-weight: 800;
}

.qks-chip--active .qks-chip-count {
  background: rgba(29, 78, 216, 0.12);
  color: #1d4ed8;
}

.qks-muted {
  color: #6b7280;
  font-size: 12px;
  font-weight: 700;
}

.qks-metrics {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.qks-metric {
  border: 1px solid #e5e7eb;
  background: #f8fafc;
  border-radius: 10px;
  padding: 12px;
}

.qks-metric-label {
  color: #6b7280;
  font-size: 12px;
  font-weight: 700;
}

.qks-metric-value {
  color: #111827;
  margin-top: 8px;
  font-size: 24px;
  line-height: 28px;
  font-weight: 800;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.qks-table-wrap {
  width: 100%;
  overflow: auto;
}

.qks-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 12px;
}

.qks-th {
  text-align: left;
  padding: 10px 12px;
  color: #6b7280;
  font-weight: 800;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
  white-space: nowrap;
}

.qks-td {
  padding: 10px 12px;
  color: #111827;
  border-bottom: 1px solid #edf1f5;
  vertical-align: top;
  background: #ffffff;
  min-width: 140px;
}

.qks-td--pre {
  white-space: pre-wrap;
  word-break: break-word;
  min-width: 260px;
}

.qks-td--empty {
  color: #6b7280;
  text-align: center;
  padding: 18px 12px;
}

.qks-link {
  color: var(--n-brand);
  font-weight: 900;
  text-decoration: none;
}

.qks-link:hover {
  text-decoration: underline;
}

.qks-subtle {
  margin-top: 4px;
  color: #6b7280;
  font-size: 12px;
}

.qks-loading-row {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: #6b7280;
  font-weight: 800;
}

.qks-spinner {
  width: 14px;
  height: 14px;
  border-radius: 999px;
  border: 2px solid rgba(148, 163, 184, 0.35);
  border-top-color: rgba(148, 163, 184, 0.9);
  animation: qks-spin 0.8s linear infinite;
}

@keyframes qks-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1024px) {
  .qks-shell {
    padding: 12px 0 32px;
  }

  .qks-field--span-8,
  .qks-field--span-4 {
    grid-column: span 12;
  }
}

@media (max-width: 640px) {
  .qks-header {
    padding: 12px 0;
  }
  .qks-save-btn {
    display: none;
  }
  .qks-metrics {
    grid-template-columns: 1fr;
  }
}

/* Keyword Search page is always "white report" (hardcoded colors — dark mode unchanged) */
::global(body.dark) .qks-page,
::global(body.dark) .qks-shell,
::global(body.dark) .qks-header,
::global(body.dark) .qks-card,
::global(body.dark) .qks-metric,
::global(body.dark) .qks-input,
::global(body.dark) .qks-th,
::global(body.dark) .qks-td {
  background: #ffffff !important;
  color: #111827 !important;
}
</style>
